import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getCurrentSessionUID } from '@selectors/quizTaking';
import useFinishQuiz from '@cont/Quiz/Taking/Common/useFinishQuiz';
import useFailedAPIQueue from '@cont/Quiz/Taking/Common/useFailedAPIQueue';
import { useProctorContext } from './ProctorProvider';

// Watches for the BeExpert SDK forcibly ending the session because the
// candidate exceeded the allowed proctoring violations, and auto-submits
// the test in response (unlike a timer expiry, this always ends the whole
// test - not just the current section - since the SDK has already decided
// the attempt is over).
const useProctorViolationTermination = () => {
  const proctorContext = useProctorContext();
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const { handleEndTest, handleCleanUpAfterFinish } = useFinishQuiz();
  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const [showViolationDialog, setShowViolationDialog] = useState(false);

  // Serializes the finish pipeline across its two triggers (the
  // session:terminated event and the dialog's Okay button) so finishQuiz is
  // never dispatched twice. Refs, not effect-local flags: the subscribe
  // effect re-runs on every render (its handler deps aren't memoized), and
  // an effect-local flag would reset each time.
  const isFinalizingRef = useRef(false);
  const hasFinalizedRef = useRef(false);

  // The finalized/in-flight latches only apply to the attempt they ran for.
  // This hook lives at Layout level and survives re-attempts on the same
  // page, so without this reset a finalize from a PREVIOUS attempt leaves
  // hasFinalizedRef latched true and the next violation's auto-submit is
  // silently skipped (dialog shows but the test never finishes).
  useEffect(() => {
    if (currentSessionUID) {
      hasFinalizedRef.current = false;
      isFinalizingRef.current = false;
    }
  }, [currentSessionUID]);

  const finalizeTerminatedTest = useCallback(async () => {
    if (isFinalizingRef.current || hasFinalizedRef.current) return;
    isFinalizingRef.current = true;

    try {
      // Flushing queued answer submissions is best-effort - it must never
      // block the finish call itself.
      try {
        await forceProcessAPISImmediately();
      } catch {
        // no-op
      }

      // skipProctorStop: session:terminated means the SDK already ended the
      // session - stop() would POST /complete on a dead session and fail or
      // hang, holding finishQuiz hostage behind it (the provider's status
      // state can still read 'running' at this point, so handleEndTest's
      // own terminal-status check can't catch this).
      await handleEndTest({ skipProctorStop: true });
      await handleCleanUpAfterFinish();
      hasFinalizedRef.current = true;
    } catch {
      // finishQuiz failed (handleEndTest already showed the snackbar) -
      // leave hasFinalizedRef false so Okay retries.
    } finally {
      isFinalizingRef.current = false;
    }
  }, [forceProcessAPISImmediately, handleEndTest, handleCleanUpAfterFinish]);

  // Okay must never drop the candidate back into a terminated test: if the
  // auto-submit hasn't gone through (finish API failed or is still
  // in-flight), retry it and keep the dialog up until it succeeds.
  const closeViolationDialog = useCallback(async () => {
    if (!hasFinalizedRef.current) {
      await finalizeTerminatedTest();
    }

    if (hasFinalizedRef.current) {
      setShowViolationDialog(false);
    }
  }, [finalizeTerminatedTest]);

  useEffect(() => {
    const on = proctorContext?.on;
    if (typeof on !== 'function' || !currentSessionUID) return undefined;

    const unsubscribe = on('session:terminated', () => {
      setShowViolationDialog(true);
      finalizeTerminatedTest();
    });

    return () => unsubscribe?.();
  }, [proctorContext?.on, currentSessionUID, finalizeTerminatedTest]);

  return { showViolationDialog, closeViolationDialog };
};

export default useProctorViolationTermination;
